import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';
import {
  CreatePurchaseRequestDTO,
  EncounterFinancialDetailsDTO,
  EncounterRefundViewModel,
  EncounterSummaryViewModel,
  type EncounterWithPatientPaymentInfoViewModel,
  MoveToCreditLogViewModel,
  OnlinePaymentRequestDTO,
  PatientEncounterFinancialDTO,
  PatientPaymentRefundViewModel,
  PatientPaymentRequestDTO,
  SquareTerminalPaymentMethodDTO,
} from 'dtos';
import {
  formatDate,
  formatISODateTime,
} from 'utils/date';
import dayjs from 'utils/dayjs';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  PaymentForm,
} from 'views/EMR/Billing/PaymentDialog';
import {
  EncounterFinancialDetails,
} from 'views/EMR/Billing/columns';
import {
  PurchaseForm,
} from 'views/EMR/Billing/CreatePurchaseDialog';
import {
  CreatePaymentForm,
} from 'views/EMR/hooks/billing';
import {
  isEmptyString,
  isNilOrZero,
  roundNumber,
} from '@populateui/utils';
import {
  EncounterWithPayment,
} from 'views/EMR/Billing/AdjustPayment';

export function isCheckLikePaymentMethod(discriminator?: string | null): boolean {
  return /(^check|collectionagencycheckpayment$)/i.test(discriminator ?? '');
}

export function serializeOnlinePaymentForm({
  currentEncounterPayment = 0,
  depositPayment = 0,
  pastDuePayment = 0,
  includesCopay = false,
  ...data
}: OnlinePaymentRequestDTO): OnlinePaymentRequestDTO {
  return {
    ...data,
    currentEncounterPayment: Number(currentEncounterPayment),
    depositPayment: Number(depositPayment),
    pastDuePayment: Number(pastDuePayment),
    includesCopay,
  };
}

export function serializePaymentForm({
  paymentMethod,
  currentEncounterPayment = 0,
  depositPayment = 0,
  pastDuePayment = 0,
  patientId,
  encounterId,
  isCardPresent,
  transactionId,
  comment,
  includesCopay = false,
}: PaymentForm): PatientPaymentRequestDTO {
  const deviceId = (paymentMethod as SquareTerminalPaymentMethodDTO)?.deviceId ?? null;
  return {
    discriminator: paymentMethod?.discriminator ?? '--',
    currentEncounterPayment: Number(currentEncounterPayment),
    depositPayment: Number(depositPayment),
    pastDuePayment: Number(pastDuePayment),
    patientId,
    encounterId,
    deviceId,
    transactionId,
    sendToTerminalDevice: !isNil(deviceId),
    isCardPresent: (isCardPresent?.value ?? 'no') === 'yes',
    paymentDate: formatISODateTime(dayjs(), false),
    includesCopay,
    comment: isEmptyString(comment) ? null : comment,
  };
}

export function withSuggestions(
  data: EncounterFinancialDetailsDTO[] = [],
  suggestions: PatientEncounterFinancialDTO[] = [],
): EncounterFinancialDetails[] {
  const suggestionsByEncounterId = keyBy(suggestions, 'encounterId');
  return data.map((item) => ({
    ...item,
    financials: {
      ...item.financials,
      ...(suggestionsByEncounterId[item?.encounterId ?? 0] ?? {}),
    },
  }));
}

export function serializePurchaseForm({
  patient: { patientId } = {},
  encounter,
  customServiceCodes,
  doctor,
  serviceFacility,
  ...formData
}: PurchaseForm): CreatePurchaseRequestDTO {
  const encounterId = encounter?.encounterId ?? 0;
  return {
    paymentRequest: serializePaymentForm({
      ...formData,
      patientId,
      encounterId,
    }),
    newAppointment: (!isNil(doctor) && !isNil(serviceFacility)
      ? {
        userId: doctor?.userId,
        serviceFacilityId: serviceFacility?.serviceFacilityId,
      }
      : undefined),
    patientId,
    encounterId,
    customServiceCodes,
  };
}

export function serializePayment(
  data: CreatePaymentForm,
  isPurchase: boolean = false,
): PatientPaymentRequestDTO {
  return isPurchase
    ? serializePurchaseForm(data as PurchaseForm)
    : serializePaymentForm(data as PaymentForm);
}

export function normalizeEncounterWithPatientPaymentInfoViewModel(
  data: EncounterWithPatientPaymentInfoViewModel[],
): EncounterWithPayment[] {
  return data.map((item) => ({
    ...item,
    suggestedAmount: Math.max(
      roundNumber((item.totalPatientPaid ?? 0) - (item.patientResponsibility ?? 0)),
      0,
    ),
  }));
}

export function normalizeMoveToCreditLogViewModel(
  data: MoveToCreditLogViewModel[],
): MoveToCreditLogViewModel[] {
  let lastDate: string | null = null;
  return data.map((item) => {
    const itemDate = formatDate(item.moveToCreditDate ?? '');
    if (itemDate !== lastDate) {
      lastDate = itemDate;
      return item;
    }
    return { ...item, moveToCreditDate: '' };
  });
}

export type RefundRow = EncounterSummaryViewModel & {
  isCredit?: boolean;
  refundedAmount?: number;
  createdOn?: string;
  refundedByUserName?: string;
  refundedByUserId?: number;
};

function normalizeRefundEncounterViewModel(
  encounters: EncounterRefundViewModel[],
  creditRefundAmount?: number,
  createdOn?: string,
  refundedBy?: string,
  refundedByUserId?: number,
): RefundRow[] {
  const normalizedEncounters = encounters?.map(
    (encounter, index) => ({
      ...encounter.encounter,
      refundedAmount: encounter.refundedAmount,
      refundedByUserName: refundedBy,
      refundedByUserId,
      ...(index === 0 && isNilOrZero(creditRefundAmount) ? { createdOn } : {}),
    }),
  );

  return isNilOrZero(creditRefundAmount)
    ? normalizedEncounters
    : [{
      isCredit: true,
      refundedAmount: creditRefundAmount,
      createdOn,
      refundedByUserName: refundedBy,
      refundedByUserId,
    },
    ...normalizedEncounters,
    ];
}

export function normalizePatientPaymentRefundViewModel(
  data: PatientPaymentRefundViewModel[],
): RefundRow[] {
  return data?.map((
    item,
  ) => normalizeRefundEncounterViewModel(
    item?.encounterRefunds ?? [],
    item?.creditRefundAmount,
    item?.createdOn,
    getDisplayName(item?.refundedBy?.formattedNames),
    item?.refundedBy?.userId,
  )).flat();
}
